import { useState, useEffect } from 'react';
import { AdminStats, AdminActivity, AdminDashboardStatsResponse } from '../types';
import { adminDashboardService } from '../services/admin-dashboard.service';

export const useAdminDashboard = () => {
  const [stats, setStats] = useState<AdminStats>({
    totalCompanies: 0,
    totalDocuments: 0,
    totalQueries: 0,
    totalUsers: 0,
    activeCompanies: 0,
    processingDocuments: 0,
    pendingETLTasks: 0,
  });
  const [recentActivity, setRecentActivity] = useState<AdminActivity[]>([]);
  const [completeData, setCompleteData] = useState<AdminDashboardStatsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the optimized single endpoint
      const completeData = await adminDashboardService.getCompleteDashboardData();
      
      if (completeData) {
        setCompleteData(completeData);
        
        // Map to stats format
        setStats({
          totalCompanies: completeData.metrics.total_companies,
          totalDocuments: completeData.metrics.total_documents,
          totalQueries: completeData.metrics.total_queries,
          totalUsers: 0, // Not included in API response
          activeCompanies: completeData.metrics.active_companies,
          processingDocuments: completeData.metrics.processing_documents,
          pendingETLTasks: completeData.metrics.pending_etl_tasks,
        });

        // Map recent queries to activity format
        const activities: AdminActivity[] = completeData.recent_queries.map((q) => ({
          id: `query-${q.id}`,
          type: 'query' as const,
          title: 'AI Query',
          description: q.query_text.length > 60 ? q.query_text.substring(0, 60) + '...' : q.query_text,
          timestamp: q.created_at,
          metadata: {
            status: q.status,
            company_id: q.company_id,
            company_name: q.company_name,
            response_time_ms: q.response_time_ms,
          },
        }));
        
        setRecentActivity(activities);
      } else {
        // Fallback to individual endpoints if complete endpoint fails
      const [dashboardStats, activity] = await Promise.all([
        adminDashboardService.getDashboardStats(),
        adminDashboardService.getRecentActivity(),
      ]);

      setStats(dashboardStats);
      setRecentActivity(activity);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
      console.error('Error loading admin dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
  }, []);

  return {
    stats,
    recentActivity,
    completeData,
    isLoading,
    error,
    refetch: loadDashboardData,
  };
};

